import React, { useState, useEffect } from 'react';
import { AlertTriangle, Shield, CheckCircle } from 'lucide-react';
import { doc, setDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../services/firebase';
import { jsPDF } from 'jspdf';
import { formatDate } from '../../utils/helpers';

const LiabilityWaiverModal = ({ waiver, user, onAgree, clubName }) => {
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [signature, setSignature] = useState('');
  const contentRef = React.useRef(null);

  // Automatically check if content fits on screen without scrolling
  useEffect(() => {
    // Wait for a brief moment to ensure fonts and layout have painted
    const timer = setTimeout(() => {
      if (contentRef.current) {
        const element = contentRef.current;
        const bottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
        if (bottom && !hasScrolledToBottom) {
          setHasScrolledToBottom(true);
        }
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [waiver.content]);

  // Expected name from user profile
  const expectedName = user.displayName || user.name || '';

  // Check if signature matches (case-insensitive, trimmed)
  const isSignatureValid = signature.trim().toLowerCase() === expectedName.trim().toLowerCase();

  const handleScroll = (e) => {
    const element = e.target;
    const bottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50; // 50px threshold

    if (bottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const generateSignedWaiverPDF = async () => {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const maxWidth = pageWidth - 2 * margin;
    let yPosition = margin;

    // Header
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text('LIABILITY WAIVER AGREEMENT', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Version ${waiver.version}`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 5;
    pdf.text(`Effective Date: ${formatDate(waiver.effectiveDate)}`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;

    // Waiver Content
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');

    const lines = pdf.splitTextToSize(waiver.content, maxWidth);

    lines.forEach((line) => {
      if (yPosition > pageHeight - margin - 60) { // Leave space for signature at bottom
        pdf.addPage();
        yPosition = margin;
      }
      pdf.text(line, margin, yPosition);
      yPosition += 5;
    });

    // Add new page for signature if content filled the page
    if (yPosition > pageHeight - 70) {
      pdf.addPage();
      yPosition = margin;
    } else {
      yPosition += 10;
    }

    // Signature Section
    pdf.setDrawColor(200);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 2;

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('AGREEMENT AND SIGNATURE', margin, yPosition);
    yPosition += 10;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text('By signing below, I acknowledge that I have read, understood, and agree to all terms above.', margin, yPosition);
    yPosition += 10;

    // Signature line
    pdf.text('Signature:', margin, yPosition);
    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(14);
    pdf.text(signature.trim(), margin + 30, yPosition);
    yPosition += 2;
    pdf.setDrawColor(0);
    pdf.line(margin + 30, yPosition, pageWidth - margin, yPosition);
    yPosition += 8;

    // Printed name
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.text('Printed Name:', margin, yPosition);
    pdf.text(expectedName, margin + 30, yPosition);
    yPosition += 8;

    // Date signed
    pdf.text('Date Signed:', margin, yPosition);
    pdf.text(formatDate(new Date(), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }), margin + 30, yPosition);
    yPosition += 15;

    // Footer
    pdf.setFontSize(8);
    pdf.setTextColor(128);
    pdf.text('This is a legally binding electronic signature.', pageWidth / 2, pageHeight - 10, { align: 'center' });

    return pdf;
  };

  const handleAgree = async () => {
    if (!agreed) {
      setError('You must check the agreement box to proceed');
      return;
    }

    if (!isSignatureValid) {
      setError(`Please type your full name exactly as shown: "${expectedName}"`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('📝 Generating signed waiver PDF...');

      // Generate PDF
      const pdf = await generateSignedWaiverPDF();
      const pdfBlob = pdf.output('blob');

      // Upload PDF to Firebase Storage
      const timestamp = new Date().getTime();
      const fileName = `waivers/${user.uid}/waiver_v${waiver.version}_${timestamp}.pdf`;
      const storageRef = ref(storage, fileName);

      console.log('📤 Uploading PDF to Firebase Storage...');
      await uploadBytes(storageRef, pdfBlob);

      // Get download URL
      const pdfUrl = await getDownloadURL(storageRef);
      console.log('✅ PDF uploaded successfully:', pdfUrl);

      // Record the agreement
      const userWaiverRef = doc(db, 'userWaiverAgreements', user.uid);

      const agreementRecord = {
        version: waiver.version,
        agreedAt: new Date().toISOString(),
        agreedAtTimestamp: new Date().toISOString(), // Use regular timestamp for arrayUnion
        userAgent: navigator.userAgent,
        signature: signature.trim(),
        signedName: expectedName,
        pdfUrl: pdfUrl, // ← Store PDF URL
        pdfPath: fileName // ← Store storage path for future reference
      };

      await setDoc(userWaiverRef, {
        userId: user.uid,
        userEmail: user.email,
        currentVersion: waiver.version,
        agreements: arrayUnion(agreementRecord),
        updatedAt: serverTimestamp()
      }, { merge: true });

      console.log('✅ Waiver agreement recorded with PDF');
      onAgree();
    } catch (err) {
      console.error('❌ Error recording waiver agreement:', err);
      setError('Failed to record agreement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-red-600 text-white p-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Shield size={28} />
            <div>
              <h2 className="text-xl font-bold">Liability Waiver Required</h2>
              <p className="text-red-100 text-xs">Version {waiver.version}</p>
            </div>
          </div>
        </div>

        {/* Alert Banner */}
        <div className="bg-orange-50 border-b-2 border-orange-200 p-3 flex-shrink-0">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-orange-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="text-sm">
              <p className="font-bold text-orange-900 mb-1">Action Required</p>
              <p className="text-orange-800">
                {waiver.isUpdate
                  ? `Our liability waiver has been updated. You must review and agree to the new version to continue using ${clubName || 'ClubHudl'}.`
                  : clubName
                    ? `You must review and agree to our liability waiver to join ${clubName}.`
                    : 'You must review and agree to our liability waiver to use ClubHudl.'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div
          ref={contentRef}
          onScroll={handleScroll}
          className="p-6 overflow-y-auto flex-1 min-h-[300px]"
        >
          {/* Scroll to bottom indicator - Top Banner */}
          {!hasScrolledToBottom && (
            <div className="mb-3 bg-blue-50 border-2 border-blue-200 rounded-lg p-3 animate-pulse">
              <div className="flex items-center gap-3 justify-center">
                <AlertTriangle className="text-blue-600" size={18} />
                <p className="text-blue-800 font-semibold text-xs">
                  Please scroll to the bottom to continue
                </p>
                <div className="flex flex-col gap-1">
                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-bounce"></div>
                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          )}

          <div className="prose prose-sm max-w-none">
            <div className="mb-4 text-sm text-gray-600">
              <p><strong>Effective Date:</strong> {formatDate(waiver.effectiveDate)}</p>
            </div>

            <div className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed">
              {waiver.content}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 p-3 flex-shrink-0">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Signature Field */}
          <div className="mb-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Electronic Signature *
            </label>
            <div className="relative">
              <input
                type="text"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                disabled={!hasScrolledToBottom}
                placeholder={`Type your full name: ${expectedName}`}
                className={`w-full px-3 py-2 border-2 rounded-lg font-serif text-base italic transition-colors ${!hasScrolledToBottom
                    ? 'bg-gray-100 cursor-not-allowed border-gray-200'
                    : isSignatureValid
                      ? 'border-green-500 bg-green-50'
                      : signature.trim()
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                  }`}
              />
              {hasScrolledToBottom && isSignatureValid && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <CheckCircle className="text-green-600" size={18} />
                </div>
              )}
            </div>
            {!hasScrolledToBottom ? (
              <p className="text-xs text-orange-600 mt-1 font-medium">
                ⚠️ Scroll to bottom to enable signature
              </p>
            ) : signature.trim() && !isSignatureValid ? (
              <p className="text-xs text-red-600 mt-1 font-medium">
                ❌ Signature must match: "{expectedName}"
              </p>
            ) : isSignatureValid ? (
              <p className="text-xs text-green-600 mt-1 font-medium">
                ✓ Signature verified
              </p>
            ) : (
              <p className="text-xs text-gray-600 mt-1">
                Type your full name exactly as shown above
              </p>
            )}
          </div>

          <div className="mb-2">
            <label className={`flex items-start gap-2 cursor-pointer p-3 border-2 rounded-lg transition-colors text-xs ${hasScrolledToBottom ? 'hover:bg-white' : 'opacity-50 cursor-not-allowed bg-gray-50'
              }`}>
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                disabled={!hasScrolledToBottom}
                className="mt-0.5 w-4 h-4 disabled:cursor-not-allowed"
              />
              <span className="text-xs font-medium text-gray-900">
                I have read and understand the liability waiver above. I agree to all terms and conditions stated in version {waiver.version}.
              </span>
            </label>
            {!hasScrolledToBottom && (
              <p className="text-xs text-orange-600 mt-1 font-medium">
                ⚠️ You must scroll to the bottom of the waiver to enable this checkbox
              </p>
            )}
          </div>

          <div className="flex gap-3 mb-2">
            <button
              onClick={handleAgree}
              disabled={!agreed || !isSignatureValid || loading}
              className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold transition-colors text-sm"
            >
              {loading ? 'Recording Agreement...' : '✓ I Agree'}
            </button>
          </div>

          <p className="text-xs text-gray-500 text-center mt-2">
            By clicking "I Agree", you acknowledge that your agreement and electronic signature will be recorded with a timestamp for legal purposes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LiabilityWaiverModal;